// ── Status types ───────────────────────────────────────────────────────────

// "pending" is what an agent writes when it submits a single item for approval
// outside a campaign (campaign items are created as "draft"). Rows carrying it
// have always reached the frontend and always rendered — the grid's filter has
// no status allowlist — so it is listed here to describe the data, not to change
// behavior. Kept identical to WAITING_ON_OWNER_STATUSES on both sides; see
// ../utils/ownerWaitingWork.ts.
export type ContentStatus =
  | "proposed"
  | "draft"
  | "pending"
  | "scheduled"
  | "processing"
  | "published"
  | "failed"
  | "cancelled";

export type MonitorStatus = "draft" | "active" | "paused";
export type DeferredContentStatus = "draft" | "active";
export type InspirationStatus = "active" | "archived";

// Artifact types that ARE monitors: self-contained, activatable-on-creation,
// no schedule. They register their own runtime (IG webhook / heartbeat agent)
// when the process activates. Kept in sync with the Python MONITOR_ARTIFACT_TYPES
// in services/py/shared/models/artifact_types.py.
export const MONITOR_ARTIFACT_TYPES = new Set<string>([
  "ig_comment_watcher_class",
  "subreddit_monitor",
  "custom_monitor",
]);

// ── Edit-routing registries ────────────────────────────────────────────────
//
// A panel dispatches an edit as a flat bag of field names, so something has to
// decide whether each name belongs at the artifact root or inside `content`.
// These four registries are that decision. Every Python content model sets
// extra="forbid", so a record field that lands inside `content` makes the whole
// artifact fail validation later, when the campaign goes live — far away from
// the edit that caused it. Kept in sync with the Python models in
// services/py/shared/models/artifact_content.py and services/py/shared/models/artifacts.py.

// Root fields every artifact type carries and a panel may edit. These are the
// editable members of ArtifactBase below.
export const ARTIFACT_ROOT_EDIT_KEYS = new Set<string>([
  "user_summary",
  "description",
  "rationale",
  "chat_thread_id",
  "ref",
  "campaign_ids",
  "linked_insight_ids",
  // The browser is the only writer, and it writes this one two ways: a direct
  // root PATCH from the Content library, and a `do_edit_artifact` bag from the
  // schedule panel. Both mean the same root field, so it is routed like one.
  // No Python model declares it, which is safe here: the artifact record is a
  // raw dict merge server-side, and only `content` is validated.
  "scheduled_at",
]);

// Root fields only some artifact types declare. A type that is absent here does
// not carry these fields at all, so an edit naming one is a mistake, not a root
// write.
export const ARTIFACT_TYPE_ROOT_EDIT_KEYS: Record<
  string,
  ReadonlySet<string>
> = {
  instagram_post: new Set(["schedule", "express_metadata"]),
  digital_ad: new Set(["schedule", "express_metadata"]),
};

// Root fields whose VALUE has to be checked before it is written, not only
// whose name does. The key is the field name rather than the artifact type,
// because instagram_post and digital_ad declare the same field with the same
// type. A field with no entry here accepts any value, as before.
export const ARTIFACT_ROOT_EDIT_VALIDATORS: Record<
  string,
  (value: unknown) => boolean
> = {
  schedule: isInstagramPostSchedule,
  express_metadata: isExpressMetadata,
};

// Record fields no generic edit may write. `id`, `created_at` and the rest are
// identity and provenance. `status` is a record field too, but every status
// transition already has its own action (do_activate_artifact, do_reject_artifact,
// do_pause_artifact), so routing it here would turn a panel edit into a
// lifecycle change. `created_by_agent_id` and `process_id` say which agent
// proposed the artifact and which process owns it; re-parenting an artifact
// through a generic edit would orphan it from its process, so that needs its own
// explicit action too.
export const ARTIFACT_PROTECTED_EDIT_KEYS = new Set<string>([
  "id",
  "user_id",
  "type",
  "status",
  "content",
  "created_by_agent_id",
  "process_id",
  "created_at",
  "updated_at",
]);

// The field names each type's content model declares. Note that a few names
// appear both here and in the record sets above: ig_comment_watcher_class
// content declares `type` (the watcher's reply mode), and proposed_edit content
// declares `process_id` and `rationale`. For those types the name means the
// content field, which is why the router tests this registry first.
export const ARTIFACT_CONTENT_KEYS: Record<string, ReadonlySet<string>> = {
  instagram_post: new Set([
    "caption",
    // Legacy singular. `InstagramPostContent` migrates it to `image_urls` in a
    // `mode="before"` validator (shared/models/artifact_content.py:102-107),
    // which runs BEFORE extra="forbid", so routing it into content is safe and
    // the server converts it. Leaving it undeclared made the router reject a
    // key the server still accepts, and the only sender with a free-form bag is
    // the `edit_artifact` agent tool.
    "image_url",
    "image_urls",
    "base_image_urls",
    "slide_prompts",
    "logo_slides",
    "source_metadata",
    "image_generation",
    "last_generation_error",
    "ai_generated_slides",
    "design_brief",
    "inspiration_artifact_id",
    "reference_image_urls",
  ]),
  digital_ad: new Set([
    "headline",
    "primary_text",
    "image_urls",
    "base_image_urls",
    "image_prompt",
    "image_headline",
    "logo_slides",
    "last_generation_error",
    "ai_generated",
  ]),
  instagram_dm: new Set(["recipient_handle", "body"]),
  instagram_comment_reply: new Set([
    "comment_id",
    "comment_text",
    "commenter_username",
    "commenter_ig_scoped_id",
    "post_id",
    "post_caption",
    "post_image_url",
    "matched_config_id",
    "response_type",
    "reply_text",
    "dm_text",
    "activation_error",
    "reply_posted_at",
    "dm_sent_at",
  ]),
  email_plaintext: new Set(["subject", "body"]),
  email_html: new Set([
    "name",
    "subject",
    "email_summary",
    "body",
    "brief",
    "instance",
    "selected_template_id",
    "last_generation_error",
  ]),
  ig_comment_watcher_class: new Set([
    "name",
    "post_id",
    "campaign_id",
    "condition",
    "type",
    "requires_review",
    "reply_prompt",
    "dm_prompt",
    "alert_channel",
    "example_comment",
    "example_reply",
    "example_dm",
  ]),
  subreddit_monitor: new Set(["name", "watch_topic", "subreddits", "cadence"]),
  custom_monitor: new Set([
    "name",
    "sources",
    "instruction",
    "cadence",
    "output_mode",
    "state",
  ]),
  deferred_content: new Set([
    "instructions",
    "artifact_type",
    "expected_count",
  ]),
  proposed_edit: new Set(["process_id", "edits", "rationale"]),
  inspiration_reference: new Set([
    "topic",
    "imagery_analysis",
    "content_analysis",
    "hook",
    "pacing",
    "payoff",
    "suggestion",
    "learning",
    "learning_theme",
    "format_name",
    "why_it_works",
    "chat_opener",
    "found_by_job_for",
    "content_copy",
    "design_read",
    "design_read_media",
    "source_url",
    "platform",
    "media_kind",
    "thumbnail_url",
    "shared_at",
  ]),
};

// ── Express metadata ───────────────────────────────────────────────────────

export type ExpressMetadata = {
  post_id: string;
  attachment_ids?: string[];
  /** Stable image references used for cache comparison, not temporary access URLs. */
  image_urls_at_upload?: string[];
  /** Live Instagram media ID, resolved after the post is published via Express. */
  ig_media_id?: string;
  /** The publishDate returned by Express, set once the post reaches a terminal state. */
  publish_date?: string | null;
  /** What the last look at this Express post found, while resolving it to live
   * Instagram media. This is a running record, not a terminal result: the
   * resolver keeps trying until the attempt count and the post's own schedule
   * both say the post is unrecoverable. `attempted_at` is the last 404 that
   * counted towards that limit, so it skips the looks that arrived too close
   * together to count. */
  media_id_resolution?: {
    status: "not_found" | "published_incomplete" | "unexpected_state";
    attempted_at?: string;
    first_attempted_at?: string;
    attempts?: number;
    last_state?: string;
    last_checked_at?: string;
  };
  /** Set when Express reports a failedToPublish state; contains the error reason from Express.
   * `error` may be a plain string or a structured object depending on the Express failure type. */
  publish_error?: {
    error?: string | Record<string, unknown>;
    fail_cause?: string;
  } | null;
};

// "on_activation" means "publish when the campaign activates". It carries no
// date, so the browser turns it into a real instant at the moment it activates
// the campaign. Storing a date up front would freeze a promise that goes stale
// while the customer is still reviewing the campaign.
export type InstagramPostSchedule =
  | { kind: "absolute"; on: string; at: string }
  | { kind: "on_activation" }
  | { kind?: never; fire_at: string };

// ── Content shape types ────────────────────────────────────────────────────

export type ImageGenerationMetadata = {
  requested_count: number;
  delivered_count: number;
  failed_indices: number[];
  surviving_slide_indices?: number[];
  partial: boolean;
  /** Why each dropped slide failed, parallel to failed_indices (same order and
   * length). Each value is one of "content_safety", "service_unavailable",
   * "reference_unreadable", "no_image" (the image service took the job but never
   * sent back a picture), "rate_limited" (the image service was too busy and
   * refused the slide), "invalid_prompt" (the image service refused the slide
   * description as invalid, so retrying it unchanged cannot work), or "error".
   * Absent on older backends, so treat a missing field as "cause unknown". */
  failed_reasons?: string[];
  /** True when the only reference was a photo the agent picked from the
   * business's library, that photo could not be read, and the image(s) were
   * regenerated from the prompt alone — so the result is honest work but is not
   * anchored to the business's own photo. The counts show no shortfall in this
   * case (partial is false and requested === delivered), so this flag is the
   * only signal. Set on carousels as well as single-image posts. Absent on
   * older backends. */
  reference_dropped?: boolean;
  /** The LLM-authored `user_summary` a substitution replaced. That summary is
   * written when the creation tool is CALLED — before any image is generated —
   * so on a substitution it can still promise the library photo the delivered
   * image does not contain. The tool swaps in a deterministic accurate summary
   * and parks the model's original here, so the swap stays auditable and nothing
   * is destroyed. Empty on every artifact that was not substituted, and absent
   * on artifacts written before this field existed (ADO-1738). */
  superseded_user_summary?: string;
};

export type InstagramPostContent = {
  caption: string;
  /** Stable image references; renderers attach temporary access URLs in memory. */
  image_urls: string[];
  /** Logo-FREE version of each slide, parallel to image_urls — a logo move/re-add
   * composites onto this clean base instead of asking the model to erase a baked-in
   * mark. Missing index => no clean base, fall back to the model-strip path. */
  base_image_urls?: string[];
  slide_prompts?: string[];
  /** Every slide description the post asked for, including the slides the image
   * service never delivered. Pairs with image_generation.surviving_slide_indices,
   * while slide_prompts is narrowed to the slides that arrived and pairs with
   * image_urls. A retry compares the two to decide whether the customer edited
   * the carousel since the last render, so both lists must always describe the
   * same wording.
   *
   * Deliberately absent from ARTIFACT_CONTENT_KEYS: the redraw transform is the
   * only writer in this repo, and every other persist path carries the stored
   * value through untouched because it writes a full document read. Listing the
   * name would let a free-form artifact edit set it to anything, which destroys
   * the retry's evidence. */
  requested_slide_prompts?: string[];
  /** The pictures this carousel was generated from — an uploaded photo, or one
   * the agent picked from the business's library. A retry rebuilds its
   * references from these. */
  reference_image_urls?: string[];
  /** Per slide, whether the picture came from an image model rather than from a
   * real photo the business supplied. Parallel to image_urls. Drives Content
   * Credentials signing at publish time. */
  ai_generated_slides?: boolean[];
  /** Where an imported post came from, when the carousel was not written here.
   * Free-form, so the shape varies by importer. */
  source_metadata?: Record<string, unknown> | null;
  /** The design brief this carousel borrowed from a saved inspiration. Empty
   * for an ordinary carousel. */
  design_brief?: string;
  /** The inspiration this carousel borrowed. Retry re-reads the brief through
   * this id, so archiving that inspiration stops it steering the carousel. */
  inspiration_artifact_id?: string;
  /** The customer's own request for this carousel, in their words. The narrative
   * plan was built from it. Empty for a carousel with no saved inspiration. */
  carousel_brief?: string;
  /** 0-based indices of slides that carry the brand mark (real logo attached). */
  logo_slides?: number[];
  last_generation_error?: string | null;
  image_generation?: ImageGenerationMetadata | null;
};

export type DigitalAdContent = {
  headline: string;
  primary_text: string;
  image_urls: string[];
  /** Logo-free version of each image (see InstagramPostContent.base_image_urls). */
  base_image_urls?: string[];
  image_prompt?: string | null;
  /** The headline painted into the image itself — the words a customer reads off
   * the ad and uses to name it. Distinct from `headline`, which is the Meta Ads
   * copy line shown below the creative next to the CTA; the ad-copy skill tells
   * the model not to mirror one into the other, so the two rarely match. Absent
   * on ads created before this field existed. */
  image_headline?: string | null;
  /** 0-based image indices that carry the brand mark; single-image ads use [0] (default) or []. */
  logo_slides?: number[];
  last_generation_error?: string | null;
  /** Whether the image came from an image model rather than from a real photo
   * the business supplied. Drives Content Credentials signing at publish time;
   * a missing value is read as true. */
  ai_generated?: boolean;
};

export type DMContent = {
  recipient_handle?: string | null;
  body: string;
};

export type EmailPlaintextContent = {
  subject: string;
  body: string;
};

type EmailInstance = {
  cosmos_id: string;
  template_id: string;
  name: string;
  description: string;
};

export type EmailRecipientSource =
  | { type: "manual" }
  | { type: "csv"; filename: string; filePath: string }
  | { type: "audience"; audienceId: string; audienceName: string };

export type EmailRecipient = {
  contact_id?: string | null;
  name: string;
  email: string;
  source: EmailRecipientSource;
};

export type EmailHtmlContent = {
  name: string;
  subject: string;
  email_summary?: string | null;
  body: string;
  brief?: string;
  instance: EmailInstance | null;
  selected_template_id: string | null;
  last_generation_error?: string | null;
};

export type { EmailInstance };

export type IGCommentReplyContent = {
  comment_id: string;
  comment_text: string;
  commenter_username: string;
  commenter_ig_scoped_id: string;
  post_id: string;
  post_caption?: string | null;
  post_image_url?: string | null;
  matched_config_id: string;
  response_type: "reply-only" | "dm-only" | "reply-and-dm";
  reply_text?: string | null;
  dm_text?: string | null;
  activation_error?: string | null;
  reply_posted_at?: string | null;
  dm_sent_at?: string | null;
};

// A send is partial when one channel the watcher asked for reached Instagram and
// another did not. A stored activation_error is not enough on its own: the final
// write can fail after every channel succeeded, and the reply-only and dm-only
// watchers have no second channel to lose. So judge the two markers against the
// channels this content actually asks for. The conditions mirror the server's
// own activation, which runs a channel only when its text is present and its
// marker is absent.
//
// The bridge and the reply panel both call this. They used to carry two rules
// that disagreed, so the chat thread and the panel could describe one send two
// opposite ways.
export const isPartialCommentReplySend = (
  content: IGCommentReplyContent | null | undefined,
): boolean => {
  if (!content?.activation_error) {
    return false;
  }
  const wantsReply =
    (content.response_type === "reply-only" ||
      content.response_type === "reply-and-dm") &&
    !!content.reply_text;
  const wantsDm =
    (content.response_type === "dm-only" ||
      content.response_type === "reply-and-dm") &&
    !!content.dm_text;
  const replySent = wantsReply && !!content.reply_posted_at;
  const dmSent = wantsDm && !!content.dm_sent_at;
  const sentSome = replySent || dmSent;
  const missedSome = (wantsReply && !replySent) || (wantsDm && !dmSent);
  return sentSome && missedSome;
};

export type IGCommentWatcherClassContent = {
  name: string;
  post_id?: string | null;
  campaign_id?: string | null;
  condition: string;
  type: "reply-only" | "dm-only" | "reply-and-dm" | "alert-only";
  // false = automated (auto-publish). New watchers bake false at creation; an
  // absent value is treated as review-first everywhere it is read.
  requires_review?: boolean;
  reply_prompt?: string | null;
  dm_prompt?: string | null;
  alert_channel?: string | null;
  example_comment?: string | null;
  example_reply?: string | null;
  example_dm?: string | null;
};

export type DeferredContentSpec = {
  instructions: string;
  artifact_type: string;
  expected_count: number | null;
};

export type SubredditEntry = {
  subreddit: string;
  rationale?: string;
  last_polled_at?: string | null;
  last_seen_post_id?: string | null;
};

export type SubredditMonitorContent = {
  name: string;
  watch_topic: string;
  subreddits: SubredditEntry[];
  cadence?: "daily" | "twice_daily";
};

export type InspirationReferenceContent = {
  topic: string;
  imagery_analysis: string;
  content_analysis: string;
  /** The per-section read of the post. Absent on a record saved before these
   * existed, and on an uploaded inspiration, which reads a design rather than
   * a post. */
  hook?: string;
  pacing?: string;
  payoff?: string;
  /** Rendered as "What to borrow". */
  suggestion?: string;
  /** The transferable lesson: what works here, and why, stated so another
   * business can apply it. This is the durable value of a find, and it is what
   * later content generation recalls. Empty on a record saved before it
   * existed, so every reader must fall back rather than draw a blank line. */
  learning?: string;
  /** A short lower-case label for the CONCEPT of the lesson, such as
   * "social proof" or "founder story". Learnings group by this, so it is a
   * concept name and never a visual format name. */
  learning_theme?: string;
  /** The name of the repeatable visual format, such as "side-by-side
   * comparison" or "day in the life". Empty when the post uses no repeatable
   * format, and empty when no media reached the analysis. */
  format_name?: string;
  /** At most 3 short reasons the post works, one reason each. Never padded to
   * three: a post with one real reason carries one. */
  why_it_works?: string[];
  /** The opening message the customer reads when they open this find, written
   * by the analysis model and rendered VERBATIM. Assembling this in the UI from
   * fixed sentences made every find read identically, which is the thing it
   * exists to avoid. Empty on a record saved before it existed. */
  chat_opener?: string;
  /** The similar business whose post this is, when the daily insights job found
   * it. Empty for a link a person shared. The home-screen feed reads this to
   * show only what the job found. */
  found_by_job_for?: string;
  content_copy: string;
  /** The exact design description read from the post's picture or video. An
   * uploaded picture and a shared link both carry one. Empty for a record
   * saved before uploads existed, and for a post whose design read failed. A
   * carousel built from this inspiration is generated from it. */
  design_read?: string;
  /** One design read per inspiration slide, in slide order (ADO-1960). A
   * multi-slide carousel is read slide by slide, because one blended read of
   * several images describes no single design. `design_read` above stays the
   * first entry, so every existing reader keeps working. Empty for a
   * single-image inspiration and for any record saved before this field
   * existed, so always fall back to `design_read`. */
  design_reads?: string[];
  /** Every image this inspiration was read FROM, in order (ADO-1960). It keeps
   * the customer's own photographs out of the generator's exclusion set, which
   * the upload resolver cannot do on its own. Empty for older records. */
  source_image_urls?: string[];
  /** Which media the backend SENT to the reading model. It records delivery,
   * not what the model understood. */
  design_read_media?: "image" | "cover_frame" | "video" | "video_uri" | "none";
  /** Empty for an uploaded inspiration, which has no public link. */
  source_url: string;
  platform: "instagram" | "tiktok" | "youtube" | "upload";
  /** Free-form label such as "carousel", "reel", "video". Display-only in the
   * UI. The backend design read branches on it to choose the video rung. */
  media_kind?: string;
  /** Empty when the source post had no usable cover image. */
  thumbnail_url?: string;
  /** ISO 8601 timestamp of when the user shared the link. */
  shared_at: string;
};

// Which read-only sensing tool a monitor source runs, and what it targets
// (a URL, a search query, a subreddit name, or an Instagram handle). Kept in
// sync with the Python MonitorSource in services/py/shared/models/artifact_content.py.
export type MonitorSource = {
  tool:
    | "scrape_website"
    | "web_search"
    | "reddit_top_posts"
    | "scrape_instagram";
  target: string;
};

// How often a monitor checks its sources. `at` is a "HH:MM" local time; it is
// ignored for the "hourly" preset (which has no fixed minute).
export type MonitorCadence = {
  preset: "hourly" | "daily" | "twice_daily" | "weekdays" | "weekly";
  at: string | null;
};

// The monitor's running cursor: what it saw last, so a later tick can tell what
// actually changed. No page snapshots are stored — only a hash, the keys it has
// already reported, and free-form notes.
export type MonitorState = {
  last_run_at: string | null;
  last_content_hash: string | null;
  last_seen_keys: string[];
  notes: string | null;
};

export type CustomMonitorContent = {
  name: string;
  sources: MonitorSource[];
  instruction: string;
  cadence: MonitorCadence;
  output_mode: "chat_alert" | "insight";
  state: MonitorState;
};

// ── Artifact base ──────────────────────────────────────────────────────────

interface ArtifactBase {
  id: string;
  user_id: string;
  user_summary?: string;
  description: string;
  rationale: string;
  created_by_agent_id?: string | null;
  process_id?: string | null;
  campaign_ids?: string[];
  ref?: string | null;
  chat_thread_id?: string | null;
  linked_insight_ids: string[];
  created_at: string;
  /** When the owner scheduled this artifact. The browser is the only writer, so
   * a row scheduled before this field existed has none. Treat absent as normal. */
  scheduled_at?: string | null;
  updated_at: string;
  /** The Cosmos ETag of the read that produced this object. Hand it back to
   * `patchArtifact` to make the write conditional on nothing having changed
   * since. Absent when the artifact came from anywhere but a single-item
   * read, so a caller must treat it as optional. */
  _etag?: string;
}

// ── Per-type subtypes ──────────────────────────────────────────────────────

export type InstagramPost = ArtifactBase & {
  type: "instagram_post";
  status: ContentStatus;
  content: InstagramPostContent;
  schedule?: InstagramPostSchedule | null;
  express_metadata?: ExpressMetadata | null;
};

export type DigitalAd = ArtifactBase & {
  type: "digital_ad";
  status: ContentStatus;
  content: DigitalAdContent;
  schedule?: InstagramPostSchedule | null;
  express_metadata?: ExpressMetadata | null;
};

export type InstagramDM = ArtifactBase & {
  type: "instagram_dm";
  status: ContentStatus;
  content: DMContent;
};

export type InstagramCommentReply = ArtifactBase & {
  type: "instagram_comment_reply";
  status: ContentStatus;
  content: IGCommentReplyContent;
};

export type EmailPlaintext = ArtifactBase & {
  type: "email_plaintext";
  status: ContentStatus;
  content: EmailPlaintextContent;
};

export type EmailHtml = ArtifactBase & {
  type: "email_html";
  status: ContentStatus;
  content: EmailHtmlContent;
};

export type EmailContent = EmailHtmlContent;
export type Email = EmailPlaintext | EmailHtml;

export type IGCommentWatcherClass = ArtifactBase & {
  type: "ig_comment_watcher_class";
  status: MonitorStatus;
  content: IGCommentWatcherClassContent;
};

export type SubredditMonitor = ArtifactBase & {
  type: "subreddit_monitor";
  status: MonitorStatus;
  content: SubredditMonitorContent;
};

export type CustomMonitor = ArtifactBase & {
  type: "custom_monitor";
  status: MonitorStatus;
  content: CustomMonitorContent;
};

export type DeferredContent = ArtifactBase & {
  type: "deferred_content";
  status: DeferredContentStatus;
  content: DeferredContentSpec;
};

export type InspirationReference = ArtifactBase & {
  type: "inspiration_reference";
  status: InspirationStatus;
  content: InspirationReferenceContent;
};

export type ProposedEditTargetType =
  | "action_config"
  | "action_trigger"
  | "artifact_content"
  | "process_metadata";

export type ProposedEditItem = {
  target_type: ProposedEditTargetType;
  target_id: string;
  proposed_changes: Record<string, unknown>;
  current_snapshot?: Record<string, unknown> | null;
};

export type ProposedEditContent = {
  process_id: string;
  edits: ProposedEditItem[];
  rationale: string;
};

export type ProposedEdit = ArtifactBase & {
  type: "proposed_edit";
  status: "draft" | "applied" | "rejected";
  content: ProposedEditContent;
};

// ── Discriminated union ────────────────────────────────────────────────────

export type Artifact =
  | InstagramPost
  | DigitalAd
  | InstagramDM
  | InstagramCommentReply
  | EmailPlaintext
  | EmailHtml
  | IGCommentWatcherClass
  | SubredditMonitor
  | CustomMonitor
  | DeferredContent
  | ProposedEdit
  | InspirationReference;

export type ArtifactType = Artifact["type"];

export type ContentArtifact =
  | InstagramPost
  | DigitalAd
  | InstagramDM
  | InstagramCommentReply
  | EmailPlaintext
  | EmailHtml;

// ── Type guards ────────────────────────────────────────────────────────────

export function isInstagramPost(a: Artifact): a is InstagramPost {
  return a.type === "instagram_post";
}

export function isDigitalAd(a: Artifact): a is DigitalAd {
  return a.type === "digital_ad";
}

export function isInstagramDM(a: Artifact): a is InstagramDM {
  return a.type === "instagram_dm";
}

export function isInstagramCommentReply(
  a: Artifact,
): a is InstagramCommentReply {
  return a.type === "instagram_comment_reply";
}

export function isEmailPlaintext(a: Artifact): a is EmailPlaintext {
  return a.type === "email_plaintext";
}

export function isEmailHtml(a: Artifact): a is EmailHtml {
  return a.type === "email_html";
}

export function isIGCommentWatcherClass(
  a: Artifact,
): a is IGCommentWatcherClass {
  return a.type === "ig_comment_watcher_class";
}

export function isSubredditMonitor(a: Artifact): a is SubredditMonitor {
  return a.type === "subreddit_monitor";
}

export function isCustomMonitor(a: Artifact): a is CustomMonitor {
  return a.type === "custom_monitor";
}

export function isDeferredContent(a: Artifact): a is DeferredContent {
  return a.type === "deferred_content";
}

export function isProposedEdit(a: Artifact): a is ProposedEdit {
  return a.type === "proposed_edit";
}

export function isInspirationReference(a: Artifact): a is InspirationReference {
  return a.type === "inspiration_reference";
}

// An absolute schedule holds a calendar day and a wall-clock time, with no zone.
// The panels write exactly these two shapes, and every reader joins them with a
// `T` before it parses the result.
const SCHEDULE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SCHEDULE_TIME_PATTERN = /^\d{2}:\d{2}(:\d{2})?$/;

// The shapes above still allow a day and a time that name no real instant, such
// as `2026-13-45`, so the value has to parse as well as look right.
const isReadableInstant = (value: string): boolean =>
  !Number.isNaN(new Date(value).getTime());

// `new Date()` ROLLS an overflowed field forward instead of refusing it, so it
// answers "does some instant exist" when the question is "does THIS day exist".
// `2026-02-30` becomes March 2 and passes, while the readers disagree about it:
// `parseDateTime` throws `Value out of range`, `moment().isValid()` is false, and
// `new Date` invents a different day. A stored row like that makes `derivedMode`
// read "now" and `handleActivate` skip the row, so the post silently never
// publishes — the same blinded panel this validator exists to prevent. Compare
// each field against the calendar instead.
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isRealCalendarDay = (on: string): boolean => {
  const year = Number(on.slice(0, 4));
  const month = Number(on.slice(5, 7));
  const day = Number(on.slice(8, 10));
  if (month < 1 || month > 12 || day < 1) return false;
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const max = month === 2 && isLeap ? 29 : DAYS_IN_MONTH[month - 1]!;
  return day <= max;
};

// `24:00` is the mirror case: `moment` calls it valid, `parseDateTime` throws, and
// `new Date` rolls it to midnight the NEXT day.
const isRealWallClockTime = (at: string): boolean => {
  const hour = Number(at.slice(0, 2));
  const minute = Number(at.slice(3, 5));
  const second = at.length > 5 ? Number(at.slice(6, 8)) : 0;
  return hour <= 23 && minute <= 59 && second <= 59;
};

// `on_activation` carries no date at all, as the type above says. A bag that
// names one anyway splits the two readers: every reader in this repo gates on
// `kind` and says the post publishes when the campaign activates, while
// `_schedule_instant` in beacon reads `on` and `at` before any `kind` test and
// names a fixed instant (`chat/context_curator.py`). That is one post with two
// answers on two surfaces. These are every key a reader reads as an instant.
const SCHEDULE_INSTANT_KEYS = [
  "on",
  "at",
  "fire_at",
  "starts_at",
  "timestamp",
] as const;

// A `fire_at` needs the same calendar check as `on` and `at` do. `new Date`
// alone ROLLS an overflow forward, so `2026-02-30T10:00:00` passes it. Every
// reader of a stored `fire_at` runs moment instead, which calls that value
// invalid. `formatScheduleDate` then returns an empty string, and the row label
// interpolates it with no guard, so the customer reads "Scheduled for" with no
// date (`ui/src/spaces/chat/panels/scheduleUtils.ts`).
const ISO_INSTANT_PATTERN = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(:\d{2})?)/;

const isReadableFireAt = (value: string): boolean => {
  const parts = ISO_INSTANT_PATTERN.exec(value);
  if (!parts) return false;
  return (
    isRealCalendarDay(parts[1]!) &&
    isRealWallClockTime(parts[2]!) &&
    isReadableInstant(value)
  );
};

// True when a value is something the schedule panels and the past-due badge can
// actually read back. An agent edit arrives as an untyped bag, so a `schedule`
// under a field name the product does not have (`starts_at`) would otherwise be
// stored and leave the row unreadable to every reader of it. The field names are
// not enough on their own: an agent that knows the shape but not the formats
// writes prose such as `{on: "next Thursday", at: "10am"}`, and a reader that
// calls `toISOString` on that pair throws and takes the whole tile down. `null`
// is a real value here: both types declare the field as nullable and clearing it
// is how a row goes back to having no schedule.
export function isInstagramPostSchedule(
  value: unknown,
): value is InstagramPostSchedule | null {
  if (value === null) return true;
  if (typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.kind === "absolute") {
    const { on, at } = candidate;
    // isRealCalendarDay/isRealWallClockTime already narrow `on`/`at` to a
    // value new Date() can always parse, so isReadableInstant here would
    // never be able to return false — it's not an independent guard.
    return (
      typeof on === "string" &&
      typeof at === "string" &&
      SCHEDULE_DAY_PATTERN.test(on) &&
      SCHEDULE_TIME_PATTERN.test(at) &&
      isRealCalendarDay(on) &&
      isRealWallClockTime(at)
    );
  }
  if (candidate.kind === "on_activation")
    return SCHEDULE_INSTANT_KEYS.every((key) => candidate[key] == null);
  // The legacy shape carries a whole instant in one field, so it takes the parse
  // check but not the day and time patterns. An empty string fails it.
  if (candidate.kind === undefined)
    return (
      typeof candidate.fire_at === "string" &&
      isReadableFireAt(candidate.fire_at)
    );
  return false;
}

// `express_metadata` is the sibling root key to `schedule` on the same
// artifact types (ARTIFACT_TYPE_ROOT_EDIT_KEYS), and it had no shape check at
// all before this: a malformed value stored in silence exactly as an
// unvalidated `schedule` used to. `post_id` is the only field every stored
// shape carries; the rest are optional and populated by the Express
// resolver over time, so this checks only what a caller can be expected to
// supply up front.
export function isExpressMetadata(
  value: unknown,
): value is ExpressMetadata | null {
  if (value === null) return true;
  if (typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.post_id === "string" && candidate.post_id.length > 0;
}

/** Unanswered is the absence of a response, not a status: the gate-2 activation
 * cascade and any pause/resume rewrite `status` out from under the answer. */
// Convenience aliases used by UI components
export function isPost(a: Artifact): a is InstagramPost {
  return a.type === "instagram_post";
}

export function isEmail(a: Artifact): a is EmailPlaintext | EmailHtml {
  return a.type === "email_plaintext" || a.type === "email_html";
}

export function isIGCommentReply(a: Artifact): a is InstagramCommentReply {
  return a.type === "instagram_comment_reply";
}
